import React, { useState, useCallback, useEffect } from 'react';
import { X, Phone, Lock, User, Eye, EyeOff, ArrowRight, Sparkles, MessageSquare } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import authService, {
  normalizePhone,
  isValidSenegalPhone,
  isValidSecretCode,
  formatPhoneDisplay,
} from '../../services/authService';
import logoShynea from '../../assets/logo-shynea.png';
import './AuthModal.css';

const AuthModal = ({ isOpen, onClose, onSuccess }) => {
  const { login, register } = useAuth();

  const [mode, setMode] = useState('login');
  /** Inscription : formulaire puis vérification SMS */
  const [registerStep, setRegisterStep] = useState('form');
  const [usesSecretCode, setUsesSecretCode] = useState(true);
  const [showCredential, setShowCredential] = useState(false);
  const [formData, setFormData] = useState({
    phone: '',
    credential: '',
    confirmCredential: '',
    firstName: '',
    lastName: '',
    otpCode: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [otpHint, setOtpHint] = useState('');

  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const resetForm = () => {
    setFormData({
      phone: '',
      credential: '',
      confirmCredential: '',
      firstName: '',
      lastName: '',
      otpCode: '',
    });
    setErrors({});
    setServerError('');
    setOtpHint('');
    setShowCredential(false);
    setUsesSecretCode(true);
    setRegisterStep('form');
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      setMode('login');
      onClose();
    }
  };

  const switchMode = (next) => {
    if (loading || next === mode) return;
    setMode(next);
    setErrors({});
    setServerError('');
    setOtpHint('');
    setRegisterStep('form');
    setFormData((prev) => ({
      ...prev,
      credential: '',
      confirmCredential: '',
      otpCode: '',
      firstName: next === 'register' ? prev.firstName : '',
      lastName: next === 'register' ? prev.lastName : '',
    }));
  };

  const refreshLoginHint = useCallback(async (phone) => {
    if (!phone.trim()) return;
    const hint = await authService.getLoginHint(phone);
    if (hint.success && hint.exists) {
      setUsesSecretCode(hint.usesSecretCode);
    }
  }, []);

  const validateRegisterForm = () => {
    const newErrors = {};
    if (!formData.phone.trim()) {
      newErrors.phone = 'Numéro de téléphone requis';
    } else if (!isValidSenegalPhone(normalizePhone(formData.phone))) {
      newErrors.phone = 'Numéro sénégalais invalide (ex: 77 123 45 67)';
    }
    if (!formData.firstName.trim()) newErrors.firstName = 'Prénom requis';
    if (!formData.lastName.trim()) newErrors.lastName = 'Nom requis';
    if (!isValidSecretCode(formData.credential)) {
      newErrors.credential = 'Code à 4-6 chiffres';
    }
    if (formData.credential !== formData.confirmCredential) {
      newErrors.confirmCredential = 'Les codes ne correspondent pas';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateLogin = () => {
    const newErrors = {};
    if (!formData.phone.trim()) {
      newErrors.phone = 'Numéro de téléphone requis';
    } else if (!isValidSenegalPhone(normalizePhone(formData.phone))) {
      newErrors.phone = 'Numéro sénégalais invalide (ex: 77 123 45 67)';
    }
    if (!formData.credential) {
      newErrors.credential = usesSecretCode ? 'Code secret requis' : 'Mot de passe requis';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const sendRegisterOtp = async () => {
    setServerError('');
    setOtpHint('');
    if (!validateRegisterForm()) return;

    setLoading(true);
    try {
      const res = await authService.sendOtp(formData.phone, 'register');
      if (!res.success) {
        setServerError(res.message || 'Impossible d\'envoyer le SMS');
        return;
      }
      setFormData((prev) => ({ ...prev, otpCode: '' }));
      setRegisterStep('otp');
      setOtpHint(
        `Code envoyé au ${formatPhoneDisplay(formData.phone)}. Valide 20 minutes.`
      );
    } catch {
      setServerError('Impossible d\'envoyer le SMS');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    if (mode === 'login') {
      if (!validateLogin()) return;
      setLoading(true);
      try {
        const hint = await authService.getLoginHint(formData.phone);
        const usePin =
          hint.success && hint.exists ? !!hint.usesSecretCode : usesSecretCode;
        if (usePin !== usesSecretCode) setUsesSecretCode(usePin);
        const result = await login(formData.phone, formData.credential, usePin);

        if (result.success) {
          if (!result.mustSetSecretCode) {
            onSuccess?.();
            handleClose();
          } else {
            handleClose();
          }
        } else if (result.code === 'ACCOUNT_DEACTIVATED') {
          setServerError(
            'Compte désactivé. Réactivez-le depuis votre espace ou contactez le support.'
          );
        } else {
          setServerError(result.message || 'Une erreur est survenue');
        }
      } catch {
        setServerError('Erreur de connexion au serveur');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Inscription — étape formulaire → envoi OTP
    if (registerStep === 'form') {
      await sendRegisterOtp();
      return;
    }

    // Inscription — étape OTP → création compte
    if (!formData.otpCode || formData.otpCode.length < 4) {
      setErrors({ otpCode: 'Entrez le code reçu par SMS' });
      return;
    }

    setLoading(true);
    try {
      const result = await register({
        phone: formData.phone,
        secretCode: formData.credential,
        firstName: formData.firstName,
        lastName: formData.lastName,
        otpCode: formData.otpCode,
      });

      if (result.success) {
        onSuccess?.();
        handleClose();
      } else {
        setServerError(result.message || 'Une erreur est survenue');
      }
    } catch {
      setServerError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const forcePin =
      name === 'confirmCredential' ||
      name === 'otpCode' ||
      (name === 'credential' && (mode === 'register' || usesSecretCode));
    const maxLen = name === 'otpCode' ? 8 : 6;
    const nextValue = forcePin ? value.replace(/\D/g, '').slice(0, maxLen) : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    setServerError('');
  };

  if (!isOpen) return null;

  const isPin = mode === 'register' || usesSecretCode;
  const credentialLabel =
    mode === 'register'
      ? 'Code secret'
      : usesSecretCode
        ? 'Code secret'
        : 'Mot de passe (dernière fois)';

  const isRegisterOtp = mode === 'register' && registerStep === 'otp';
  const title = mode === 'login'
    ? 'Bon retour'
    : isRegisterOtp
      ? 'Vérification SMS'
      : 'Bienvenue';
  const subtitle = mode === 'login'
    ? 'Connectez-vous avec votre téléphone et votre code secret.'
    : isRegisterOtp
      ? 'Saisissez le code reçu par SMS pour finaliser votre compte.'
      : 'Créez votre compte client — un SMS confirmera votre numéro.';

  const submitLabel = loading
    ? 'Chargement…'
    : mode === 'login'
      ? 'Se connecter'
      : isRegisterOtp
        ? 'Créer mon compte'
        : 'Recevoir un code SMS';

  return (
    <div
      className="auth-modal-overlay"
      onClick={handleClose}
      role="presentation"
    >
      <div
        className="auth-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-title"
      >
        <button
          type="button"
          className="auth-modal-close"
          onClick={handleClose}
          disabled={loading}
          aria-label="Fermer"
        >
          <X size={18} strokeWidth={2.25} />
        </button>

        <aside className="auth-modal-brand">
          <div className="auth-brand-glow auth-brand-glow-a" />
          <div className="auth-brand-glow auth-brand-glow-b" />
          <div className="auth-brand-inner">
            <div className="auth-brand-logo-wrap">
              <img src={logoShynea} alt="" className="auth-brand-logo" />
            </div>
            <p className="auth-brand-kicker">
              <Sparkles size={14} strokeWidth={2} />
              Beauté à Dakar
            </p>
            <h2 className="auth-brand-name">Shynea</h2>
            <p className="auth-brand-tagline">
              Réservez vos soins en un instant — salons, coiffure, maquillage et
              bien-être.
            </p>
            <ul className="auth-brand-perks">
              <li>Réservation en quelques taps</li>
              <li>Paiement sécurisé de l’acompte</li>
              <li>Vos salons favoris au même endroit</li>
            </ul>
          </div>
        </aside>

        <div className="auth-modal-panel">
          <div className="auth-panel-head">
            <h2 id="auth-modal-title" className="auth-panel-title">
              {title}
            </h2>
            <p className="auth-panel-sub">{subtitle}</p>
          </div>

          {!isRegisterOtp && (
            <div className="auth-tabs" role="tablist" aria-label="Mode">
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'login'}
                className={`auth-tab${mode === 'login' ? ' is-active' : ''}`}
                onClick={() => switchMode('login')}
                disabled={loading}
              >
                Connexion
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'register'}
                className={`auth-tab${mode === 'register' ? ' is-active' : ''}`}
                onClick={() => switchMode('register')}
                disabled={loading}
              >
                Inscription
              </button>
              <span
                className="auth-tab-indicator"
                style={{ transform: `translateX(${mode === 'register' ? '100%' : '0'})` }}
                aria-hidden
              />
            </div>
          )}

          <form className="auth-modal-form" onSubmit={handleSubmit} noValidate>
            {isRegisterOtp ? (
              <>
                {otpHint && <p className="auth-otp-hint">{otpHint}</p>}
                <div className={`auth-field${errors.otpCode ? ' has-error' : ''}`}>
                  <label htmlFor="auth-otp">Code SMS</label>
                  <div className="auth-input-wrap">
                    <MessageSquare size={17} className="auth-input-ico" strokeWidth={2} />
                    <input
                      id="auth-otp"
                      type="text"
                      name="otpCode"
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      value={formData.otpCode}
                      onChange={handleChange}
                      placeholder="Code reçu"
                      maxLength={8}
                      disabled={loading}
                      autoFocus
                    />
                  </div>
                  {errors.otpCode && (
                    <span className="auth-error">{errors.otpCode}</span>
                  )}
                </div>
                <button
                  type="button"
                  className="auth-resend-btn"
                  onClick={sendRegisterOtp}
                  disabled={loading}
                >
                  Renvoyer le code
                </button>
                <button
                  type="button"
                  className="auth-back-link"
                  onClick={() => {
                    setRegisterStep('form');
                    setOtpHint('');
                    setServerError('');
                    setFormData((p) => ({ ...p, otpCode: '' }));
                  }}
                  disabled={loading}
                >
                  ← Modifier mes informations
                </button>
              </>
            ) : (
              <>
                {mode === 'register' && (
                  <div className="auth-name-row">
                    <div className={`auth-field${errors.firstName ? ' has-error' : ''}`}>
                      <label htmlFor="auth-firstName">Prénom</label>
                      <div className="auth-input-wrap">
                        <User size={17} className="auth-input-ico" strokeWidth={2} />
                        <input
                          id="auth-firstName"
                          type="text"
                          name="firstName"
                          autoComplete="given-name"
                          value={formData.firstName}
                          onChange={handleChange}
                          placeholder="Aminata"
                          disabled={loading}
                        />
                      </div>
                      {errors.firstName && (
                        <span className="auth-error">{errors.firstName}</span>
                      )}
                    </div>
                    <div className={`auth-field${errors.lastName ? ' has-error' : ''}`}>
                      <label htmlFor="auth-lastName">Nom</label>
                      <div className="auth-input-wrap">
                        <User size={17} className="auth-input-ico" strokeWidth={2} />
                        <input
                          id="auth-lastName"
                          type="text"
                          name="lastName"
                          autoComplete="family-name"
                          value={formData.lastName}
                          onChange={handleChange}
                          placeholder="Diop"
                          disabled={loading}
                        />
                      </div>
                      {errors.lastName && (
                        <span className="auth-error">{errors.lastName}</span>
                      )}
                    </div>
                  </div>
                )}

                <div className={`auth-field${errors.phone ? ' has-error' : ''}`}>
                  <label htmlFor="auth-phone">Téléphone</label>
                  <div className="auth-input-wrap">
                    <Phone size={17} className="auth-input-ico" strokeWidth={2} />
                    <span className="auth-phone-prefix">+221</span>
                    <input
                      id="auth-phone"
                      type="tel"
                      name="phone"
                      autoComplete="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      onBlur={() => mode === 'login' && refreshLoginHint(formData.phone)}
                      placeholder="77 123 45 67"
                      disabled={loading}
                    />
                  </div>
                  {errors.phone && <span className="auth-error">{errors.phone}</span>}
                </div>

                <div className={`auth-field${errors.credential ? ' has-error' : ''}`}>
                  <label htmlFor="auth-credential">{credentialLabel}</label>
                  <div className="auth-input-wrap">
                    <Lock size={17} className="auth-input-ico" strokeWidth={2} />
                    <input
                      id="auth-credential"
                      type={showCredential ? 'text' : 'password'}
                      name="credential"
                      autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                      value={formData.credential}
                      onChange={handleChange}
                      inputMode={isPin ? 'numeric' : 'text'}
                      maxLength={isPin ? 6 : undefined}
                      placeholder={isPin ? '••••' : 'Votre ancien mot de passe'}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="auth-eye"
                      onClick={() => setShowCredential((v) => !v)}
                      tabIndex={-1}
                      aria-label={showCredential ? 'Masquer' : 'Afficher'}
                    >
                      {showCredential ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                  {errors.credential && (
                    <span className="auth-error">{errors.credential}</span>
                  )}
                  {mode === 'login' && !usesSecretCode && (
                    <p className="auth-hint auth-hint--warn">
                      Utilisez votre mot de passe actuel. Vous créerez un code secret juste après.
                    </p>
                  )}
                  {isPin && (
                    <p className="auth-hint">4 à 6 chiffres — simple et sécurisé</p>
                  )}
                </div>

                {mode === 'register' && (
                  <div
                    className={`auth-field${errors.confirmCredential ? ' has-error' : ''}`}
                  >
                    <label htmlFor="auth-confirm">Confirmer le code</label>
                    <div className="auth-input-wrap">
                      <Lock size={17} className="auth-input-ico" strokeWidth={2} />
                      <input
                        id="auth-confirm"
                        type={showCredential ? 'text' : 'password'}
                        name="confirmCredential"
                        autoComplete="new-password"
                        value={formData.confirmCredential}
                        onChange={handleChange}
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="••••"
                        disabled={loading}
                      />
                    </div>
                    {errors.confirmCredential && (
                      <span className="auth-error">{errors.confirmCredential}</span>
                    )}
                  </div>
                )}
              </>
            )}

            {serverError && (
              <div className="auth-server-error" role="alert">
                {serverError}
              </div>
            )}

            <button type="submit" className="auth-submit-btn" disabled={loading}>
              <span>{submitLabel}</span>
              {!loading && <ArrowRight size={18} strokeWidth={2.25} />}
            </button>

            {!isRegisterOtp && (
              <p className="auth-switch-line">
                {mode === 'login' ? (
                  <>
                    Pas encore de compte ?{' '}
                    <button type="button" onClick={() => switchMode('register')} disabled={loading}>
                      S’inscrire
                    </button>
                  </>
                ) : (
                  <>
                    Déjà un compte ?{' '}
                    <button type="button" onClick={() => switchMode('login')} disabled={loading}>
                      Se connecter
                    </button>
                  </>
                )}
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
